import isNil from 'lodash/isNil';
import keyBy from 'lodash/keyBy';
import {
  PatientAppointmentStatusSettingDTO,
  RoomStatusDTO,
  RoomStatusUpdatedNotificationDTO,
} from 'dtos';
import {
  isEmptyString,
} from '@populateui/utils';

export type AppointmentStatusItem = {
  value: string;
  roomSettingId: number | null;
  occupiedCount: number;
};

export type AppointmentStatusByRoomId = Record<number, AppointmentStatusItem>;

export type AppointmentStatusMaps = {
  statuses: AppointmentStatusItem[];
  byRoomId: AppointmentStatusByRoomId;
  values: string[];
  filterOptions: AppointmentStatusFilterOption[];
  encounterTypeStatuses: AppointmentStatusItem[];
};

export type AppointmentStatusFilterOption = {
  displayName: string;
  patientAppointmentStatus: string;
};

function resolveStatusValue(
  setting: PatientAppointmentStatusSettingDTO,
): string {
  return setting.patientAppointmentStatus ?? setting.displayName ?? '';
}

function buildEncounterTypeStatuses(
  statuses: AppointmentStatusItem[],
): AppointmentStatusItem[] {
  return statuses.filter(
    (status) => !/note finalized/i.test(status.value),
  );
}

export function normalizePatientAppointmentStatusSettings(
  statusSettings: PatientAppointmentStatusSettingDTO[],
): AppointmentStatusFilterOption[] {
  return statusSettings.flatMap((setting) => {
    const patientAppointmentStatus = resolveStatusValue(setting);

    return isEmptyString(patientAppointmentStatus) ? [] : [{
      displayName: setting.displayName ?? patientAppointmentStatus,
      patientAppointmentStatus,
    }];
  });
}

function buildByRoomId(
  statuses: AppointmentStatusItem[],
): AppointmentStatusByRoomId {
  const roomsOnly = statuses.filter(
    (status) => !isNil(status.roomSettingId),
  );
  return keyBy(roomsOnly, 'roomSettingId') as AppointmentStatusByRoomId;
}

function buildDerivedLists(
  statusSettings: PatientAppointmentStatusSettingDTO[],
  statuses: AppointmentStatusItem[],
): Pick<AppointmentStatusMaps, 'values' | 'filterOptions' | 'encounterTypeStatuses'> {
  return {
    values: statuses.map((status) => status.value),
    filterOptions: normalizePatientAppointmentStatusSettings(statusSettings),
    encounterTypeStatuses: buildEncounterTypeStatuses(statuses),
  };
}

export function normalizeAppointmentStatus(
  statusSettings: PatientAppointmentStatusSettingDTO[],
  roomStatuses: RoomStatusDTO[],
): AppointmentStatusMaps {
  const roomByName = keyBy(
    roomStatuses.filter((room) => !isNil(room.name)),
    (room) => room.name as string,
  );

  const statuses = statusSettings.map((setting) => {
    const value = resolveStatusValue(setting);
    const room = roomByName[value];
    return {
      value,
      roomSettingId: room?.roomSettingId ?? null,
      occupiedCount: room?.occupiedCount ?? 0,
    };
  });

  return {
    statuses,
    byRoomId: buildByRoomId(statuses),
    ...buildDerivedLists(statusSettings, statuses),
  };
}

function withUpdatedCount(
  item: AppointmentStatusItem,
  delta: number,
): AppointmentStatusItem {
  return {
    ...item,
    occupiedCount: Math.max(0, item.occupiedCount + delta),
  };
}

export function applyRoomOccupancyUpdate(
  maps: AppointmentStatusMaps,
  notification: RoomStatusUpdatedNotificationDTO,
): AppointmentStatusMaps {
  const { fromRoomSettingId, toRoomSettingId } = notification;
  const { byRoomId } = maps;
  let nextStatuses = maps.statuses;

  if (!isNil(fromRoomSettingId) && !isNil(byRoomId[fromRoomSettingId])) {
    nextStatuses = nextStatuses.map((status) => (
      status.roomSettingId === fromRoomSettingId
        ? withUpdatedCount(status, -1)
        : status
    ));
  }

  if (!isNil(toRoomSettingId) && !isNil(byRoomId[toRoomSettingId])) {
    nextStatuses = nextStatuses.map((status) => (
      status.roomSettingId === toRoomSettingId
        ? withUpdatedCount(status, 1)
        : status
    ));
  }

  return {
    statuses: nextStatuses,
    byRoomId: buildByRoomId(nextStatuses),
    values: maps.values,
    filterOptions: maps.filterOptions,
    encounterTypeStatuses: buildEncounterTypeStatuses(nextStatuses),
  };
}
